import { useEffect, useMemo, useState } from "react";
import { useCityAutocomplete } from "./useCityAutocomplete";
import { useRecentSearches } from "../components/searshForms/searchForm/hooks/useRecentSearches";

const toLocalDateFromAny = (val) => {
  if (!val) return null;
  if (val instanceof Date) {
    return Number.isNaN(val.getTime()) ? null : val;
  }

  if (typeof val === "string") {
    const parts = val.split("-");
    if (parts.length === 3) {
      const [y, m, d] = parts.map(Number);
      if (y && m && d) {
        const date = new Date(y, m - 1, d);
        return Number.isNaN(date.getTime()) ? null : date;
      }
    }
    const d2 = new Date(val);
    return Number.isNaN(d2.getTime()) ? null : d2;
  }

  const d3 = new Date(val);
  return Number.isNaN(d3.getTime()) ? null : d3;
};

const buildRecentDateLabel = (range) => {
  if (typeof range === "string") return range;
  if (!Array.isArray(range) || range.length < 2) return "";
  const [from, to] = range;
  const fromDate = toLocalDateFromAny(from);
  const toDate = toLocalDateFromAny(to);
  if (!fromDate && !toDate) return "";
  const fmt = new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  if (fromDate && toDate) {
    const sameMonth =
      fromDate.getMonth() === toDate.getMonth() &&
      fromDate.getFullYear() === toDate.getFullYear();
    if (sameMonth) {
      return `${fromDate.getDate()}–${toDate.getDate()} ${fmt.format(fromDate)
        .replace(/^\d+\s*/u, "")
        .replace(/\s+$/, "")}`;
    }
    return `${fmt.format(fromDate)} – ${fmt.format(toDate)}`;
  }
  return fmt.format(fromDate || toDate);
};

export const useDestinationSearchSelect = ({
  destination,
  setDestination,
  destinationQuery,
  setDestinationQuery,
  onApplyRecent,
  recentMax = 6,
}) => {
  const [autoCompleteQuery, setAutoCompleteQuery] = useState("");
  const [selectedDestinationOption, setSelectedDestinationOption] = useState(null);

  const {
    results: cityResults,
    loading: isCityLoading,
    hasRequested: hasCityRequested,
  } = useCityAutocomplete(autoCompleteQuery);
  const { recentSearches, addRecentSearch } = useRecentSearches({ max: recentMax });

  useEffect(() => {
    setDestinationQuery(destination || "");
    if (destination) {
      setSelectedDestinationOption({
        label: destination,
        value: destination,
      });
    }
  }, [destination, setDestinationQuery]);

  const destinationOptions = useMemo(
    () =>
      cityResults.map((item) => ({
        label: item.country ? `${item.city}, ${item.country}` : item.city,
        value: item.country ? `${item.city}, ${item.country}` : item.city,
      })),
    [cityResults]
  );

  const recentOptions = useMemo(
    () =>
      recentSearches.map((item) => ({
        label: item.label || item.destination || "",
        value: item.value || item.destination || "",
        dateRange: buildRecentDateLabel(item.dateRange),
        dateLabel: item.dateLabel || buildRecentDateLabel(item.dateRange),
        startDate: item.startDate,
        endDate: item.endDate,
        travelers: item.travelers,
      })),
    [recentSearches]
  );

  const trimmedDestinationQuery = destinationQuery.trim();
  const effectiveDestinationOptions = trimmedDestinationQuery
    ? destinationOptions
    : [];
  const selectedOptionFallback =
    selectedDestinationOption &&
    !destinationOptions.some((opt) => opt.value === selectedDestinationOption.value)
      ? [selectedDestinationOption]
      : [];
  const lookupDestinationOptions =
    effectiveDestinationOptions.length > 0
      ? effectiveDestinationOptions
      : selectedOptionFallback;
  const shouldHideDestinationPopover =
    !hasCityRequested && !isCityLoading && recentOptions.length === 0;

  const handleDestinationInput = (e) => {
    const next = e?.target?.value ?? "";
    setDestinationQuery(next);
    setAutoCompleteQuery(next);
  };

  const handleDestinationChange = (value) => {
    const next = value || "";
    setDestination(next);
    const selected = destinationOptions.find((opt) => opt.value === next);
    setDestinationQuery(selected?.label || next);
    setAutoCompleteQuery("");
    if (selected) {
      setSelectedDestinationOption(selected);
    } else if (next) {
      setSelectedDestinationOption({ label: next, value: next });
    }
  };

  const handleSelectRecent = (opt, { source } = {}) => {
    if (source !== "recent") return;
    setDestination(opt.value || opt.label || "");
    setDestinationQuery(opt.label || opt.value || "");
    setAutoCompleteQuery("");
    setSelectedDestinationOption({
      label: opt.label || opt.value || "",
      value: opt.value || opt.label || "",
    });
    onApplyRecent?.(opt);
  };

  return {
    isCityLoading,
    hasCityRequested,
    destinationOptions,
    recentOptions,
    lookupDestinationOptions,
    shouldHideDestinationPopover,
    handleDestinationInput,
    handleDestinationChange,
    handleSelectRecent,
    addRecentSearch,
    buildRecentDateLabel,
    toLocalDateFromAny,
  };
};
